import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { useIsCurrentUserAdmin } from "hooks/auth";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const isAdminUser = useIsCurrentUserAdmin();
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;

	const isAccountSettingsActive = currentPath.includes("account-settings");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");

	const hasChildren = (hasRfxSettings && isProcurer) || isProcurer;

	if (!isAdminUser) {
		return null;
	}

	if (!hasChildren) {
		return (
			<AppMenuItem
				name="Settings"
				icon={<FiSettings size={24} />}
				link={accountSettingsLink}
				isCollapsed={isCollapsed}
				testId="account-settings"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
		);
	}

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={accountSettingsLink}
			testId="settings-menu"
			tourName="AccountSettings"
			onNavigate={onNavigate}
		>
			<ExpandableMenuChildItem
				name="Account Settings"
				link={accountSettingsLink}
				isActive={isAccountSettingsActive}
				testId="account-settings-child"
				onNavigate={onNavigate}
			/>
			{hasRfxSettings && isProcurer && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{isProcurer && (
				<ExpandableMenuChildItem
					name="PDF Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
